import type { ReactNode } from 'react';
import type { AlertProps as MuiAlertProps } from '@mui/material/Alert';
import MuiAlert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';

interface AlertProps {
  variant?: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  children: ReactNode;
  onClose?: () => void;
  dismissible?: boolean;
  icon?: React.ReactNode;
  sx?: MuiAlertProps['sx'];
  className?: string;
}

const StyledAlert = styled(MuiAlert)(({ theme, severity }) => {
  const colorMap = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const color = colorMap[severity || 'info'];

  return {
    borderRadius: 10,
    border: `1px solid ${alpha(color, 0.2)}`,
    backgroundColor: alpha(color, 0.06),
    padding: '12px 16px',
    marginBottom: 16,
    fontFamily: '"IBM Plex Sans", sans-serif',
    '& .MuiAlert-icon': {
      color: color,
      opacity: 1,
      fontSize: '1.25rem',
    },
    '& .MuiAlert-message': {
      padding: '2px 0',
      fontSize: '0.875rem',
    },
    '& .MuiAlertTitle-root': {
      fontFamily: '"IBM Plex Sans", sans-serif',
      fontWeight: 600,
      marginBottom: 4,
      color: theme.palette.text.primary,
    },
    '& .MuiAlert-action': {
      paddingTop: 0,
      alignItems: 'flex-start',
    },
  };
});

export function Alert({
  variant = 'info',
  title,
  children,
  onClose,
  dismissible = false,
  icon,
  sx,
  className,
}: AlertProps) {
  return (
    <Collapse in={true}>
      <StyledAlert
        severity={variant}
        icon={icon}
        sx={sx}
        className={className}
        action={
          (onClose || dismissible) && (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={onClose}
              sx={{
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )
        }
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </StyledAlert>
    </Collapse>
  );
}
